import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { useLocale } from "@/contexts/LocaleContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PhotoUploader from "@/components/PhotoUploader";
import LocationPicker from "@/components/LocationPicker";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const TYPES = ["casa", "apartamento", "villa", "local", "terreno", "oficina"];

const empty = {
  title: "",
  price: "",
  currency: "USD",
  address: "",
  bedrooms: "",
  bathrooms: "",
  square_meters: "",
  property_type: "casa",
  transaction_type: "venta",
  description: "",
  photos: [],
  latitude: null,
  longitude: null,
};

export default function PropertyForm() {
  const { t } = useLocale();
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    api.get(`/properties/${id}`).then(({ data }) => {
      setForm({
        title: data.title, price: data.price, currency: data.currency,
        address: data.address, bedrooms: data.bedrooms, bathrooms: data.bathrooms || 0,
        square_meters: data.square_meters, property_type: data.property_type,
        transaction_type: data.transaction_type || "venta", description: data.description || "",
        photos: data.photos || [],
        latitude: typeof data.latitude === "number" ? data.latitude : null,
        longitude: typeof data.longitude === "number" ? data.longitude : null,
      });
    }).catch((e) => toast.error(apiErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        bedrooms: Number(form.bedrooms),
        bathrooms: Number(form.bathrooms || 0),
        square_meters: Number(form.square_meters),
        latitude: form.latitude != null ? Number(form.latitude) : null,
        longitude: form.longitude != null ? Number(form.longitude) : null,
      };
      if (isEdit) {
        await api.put(`/properties/${id}`, payload);
        toast.success("Propiedad actualizada");
      } else {
        await api.post("/properties", payload);
        toast.success("Propiedad publicada");
      }
      navigate("/dashboard");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-16 text-center text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div className="mx-auto max-w-3xl px-6 md:px-10 pt-12 md:pt-20 pb-16">
      <div className="mb-8">
        <div className="label-eyebrow text-secondary">{t("nav.dashboard")}</div>
        <h1 className="mt-2 font-serif text-5xl">{isEdit ? t("form.edit_title") : t("form.new_title")}</h1>
      </div>

      <form onSubmit={submit} className="space-y-6" data-testid="property-form">
        <div>
          <label className="label-eyebrow block mb-2">{t("form.f_title")}</label>
          <Input required value={form.title} onChange={set("title")} data-testid="form-title" />
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="label-eyebrow block mb-2">{t("form.f_type")}</label>
            <Select value={form.property_type} onValueChange={(v) => set("property_type")(v)}>
              <SelectTrigger data-testid="form-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPES.map((tp) => <SelectItem key={tp} value={tp}>{t(`types.${tp}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">{t("form.f_transaction")}</label>
            <Select value={form.transaction_type} onValueChange={(v) => set("transaction_type")(v)}>
              <SelectTrigger data-testid="form-transaction"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="venta">{t("filters.sale")}</SelectItem>
                <SelectItem value="alquiler">{t("filters.rent")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">{t("form.f_currency")}</label>
            <Select value={form.currency} onValueChange={(v) => set("currency")(v)}>
              <SelectTrigger data-testid="form-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD $</SelectItem>
                <SelectItem value="DOP">DOP RD$</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="label-eyebrow block mb-2">{t("form.f_price")}</label>
            <Input required type="number" min="0" value={form.price} onChange={set("price")} data-testid="form-price" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">{t("form.f_address")}</label>
            <Input required value={form.address} onChange={set("address")} data-testid="form-address" />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="label-eyebrow block mb-2">{t("form.f_bedrooms")}</label>
            <Input required type="number" min="0" value={form.bedrooms} onChange={set("bedrooms")} data-testid="form-bedrooms" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">{t("form.f_bathrooms")}</label>
            <Input type="number" min="0" value={form.bathrooms} onChange={set("bathrooms")} data-testid="form-bathrooms" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">{t("form.f_sqm")}</label>
            <Input required type="number" min="1" step="0.01" value={form.square_meters} onChange={set("square_meters")} data-testid="form-sqm" />
          </div>
        </div>

        <div>
          <label className="label-eyebrow block mb-2">{t("form.f_description")}</label>
          <Textarea rows={5} value={form.description} onChange={set("description")} data-testid="form-description" />
        </div>

        <div className="border-t border-border pt-6">
          <PhotoUploader photos={form.photos} onChange={(next) => setForm((p) => ({ ...p, photos: next }))} />
        </div>

        <div className="border-t border-border pt-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-eyebrow">{t("form.f_location")}</div>
              <p className="text-xs text-muted-foreground mt-1">{t("form.f_location_hint")}</p>
            </div>
            {form.latitude != null && form.longitude != null && (
              <div className="text-xs text-muted-foreground font-mono" data-testid="form-coords">
                {form.latitude.toFixed(5)}, {form.longitude.toFixed(5)}
              </div>
            )}
          </div>
          <LocationPicker
            lat={form.latitude}
            lng={form.longitude}
            onChange={(lat, lng) => setForm((p) => ({ ...p, latitude: lat, longitude: lng }))}
            height={300}
          />
        </div>

        <div className="flex gap-3 pt-2">
          <Button type="submit" disabled={saving} className="rounded-full btn-hover-lift" data-testid="form-submit">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEdit ? t("form.save_changes") : t("form.publish")}
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate("/dashboard")} data-testid="form-cancel">
            {t("form.cancel")}
          </Button>
        </div>
      </form>
    </div>
  );
}
